import type {
    TApiFileStatus,
    TApiFileType,
    TApiSubmissionHistoryResponse,
} from "@/apis/proda_finance/types";

/**
 * One upload attempt for the period, as the Submission history table renders it.
 *
 * `rejectionReason` is the reason for *both* terminal outcomes — a person's
 * rejection and a processing failure. On a rejected attempt a null
 * `rejectedByName` means the system rejected it, so the table says "automatically"
 * rather than leaving the attribution blank. `rejectedAt` is null on a failed
 * attempt, where `statusChangedAt` is the only date there is.
 */
export type TSubmissionHistoryEntry = {
    id: string;
    /** The backend `FileType` wire value, not the store slice's "tb"/"gl" shorthand. */
    fileType: TApiFileType;
    originalFilename: string;
    status: TApiFileStatus;
    createdAt: string;
    uploadedAt: string | null;
    uploadedByName: string | null;
    archivedAt: string | null;
    isActive: boolean;
    rejectionReason: string | null;
    rejectedByName: string | null;
    rejectedAt: string | null;
    statusChangedAt: string | null;
    hasErrorReport: boolean;
    totalLineItems: number;
};

export type TSubmissionHistory = {
    period: string;
    /** Newest first, exactly as the server ordered them. */
    entries: TSubmissionHistoryEntry[];
};

/**
 * Map the submission-history payload to the shape the tab renders.
 *
 * Field renaming only: no filtering, no sorting, no derived display strings.
 * The server's order *is* the chronology the tab shows, and every row it sends
 * — failed, rejected, superseded — is one the tab exists to display, so
 * dropping any here would defeat the point.
 */
export function mapApiSubmissionHistory(api: TApiSubmissionHistoryResponse): TSubmissionHistory {
    return {
        period: api.period,
        entries: api.entries.map((entry) => ({
            id: entry.id,
            fileType: entry.file_type,
            originalFilename: entry.original_filename,
            status: entry.status,
            createdAt: entry.created_at,
            uploadedAt: entry.uploaded_at,
            uploadedByName: entry.uploaded_by_name,
            archivedAt: entry.archived_at,
            isActive: entry.is_active,
            rejectionReason: entry.rejection_reason,
            rejectedByName: entry.rejected_by_name,
            rejectedAt: entry.rejected_at,
            statusChangedAt: entry.status_changed_at,
            hasErrorReport: entry.has_error_report,
            totalLineItems: entry.total_line_items,
        })),
    };
}
